import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { Collapsible } from "@/app/components/ui/collapsible";
import { FaqSection } from "@/app/components/seo-support";
import { siteHeaderActions, siteNavItems } from "@/app/content";
import { ROUTE_FAQS } from "@/app/seo-content";
import { CodeBlock, NextCta, Note, QuickStartHero, Step } from "./QuickStartShared";

const HOTLINE_ONBOARDING_BODY = `{
  "responder_id": "responder_xxx",
  "hotline_id": "your.namespace.tool-name.v1",
  "template_ref": "local.delegated-execution.workspace-summary.v1",
  "title": "工作区摘要器",
  "summary": "把指定目录的代码状态摘要成一段话，给 Agent 用",
  "input_schema": {
    "type": "object",
    "required": ["text"],
    "properties": {
      "text": { "type": "string", "description": "用户提示" }
    }
  },
  "output_schema": {
    "type": "object",
    "required": ["summary"],
    "properties": {
      "summary": { "type": "string", "description": "工作区摘要" }
    }
  }
}`;

export default function QuickStartResponder() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItems} actionItems={siteHeaderActions} homeHref="/" />

      <QuickStartHero
        role="Responder · 5 min"
        roleColor="#3B82F6"
        titleTop="把能力变成"
        titleBottom="一根 HOTLINE"
        lead="先用已发布的 delexec-ops 包跑通本地 runtime 和 example hotline，再把自己的能力挂到 supervisor。源码安装与提交审核都放在后续步骤里。"
        meta={["Local 起步", "Platform 可选", "支持 process / http"]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
        <div className="mb-8 border-4 border-black bg-white/85 backdrop-blur-sm p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">这页教你做什么</p>
          <h2 className="mt-2 text-xl font-black text-black sm:text-2xl">
            从 0 到「一根本地可调用、可提交审核的 Hotline draft」
          </h2>
          <ul className="mt-4 grid gap-2 text-sm font-bold text-zinc-800 sm:grid-cols-2">
            <li>· 启动 supervisor + 注册账号</li>
            <li>· 启用 Responder 角色</li>
            <li>· 用内置 example hotline 跑通链路</li>
            <li>· 写自己的 hotline registration draft</li>
            <li>· 生成 registration draft · 可选提交 review · 检查本地在线状态</li>
          </ul>
        </div>

        <div className="space-y-8">
          <Step
            num="01"
            title="官方路径：安装 CLI + 跑通 bootstrap"
            intent="陌生用户从已发布的 @delexec/ops 开始，先确认本机 runtime、Caller/Responder 最小闭环和 example hotline 都能工作。"
          >
            <CodeBlock language="bash" caption="任意目录">{`npm install -g @delexec/ops
delexec-ops bootstrap --email you@example.com`}</CodeBlock>

            <Note>
              这两条命令会安装 CLI 并完成本地 runtime 初始化。它不是发布流程，
              但能证明你的本机 runtime 已经具备 Responder 侧调试基础。
            </Note>

            <Collapsible title="从源码运行 / Contributor path">
              <div className="space-y-2 text-sm text-zinc-600">
                <p>如果你需要修改 CLI 源码或参与贡献：</p>
                <pre className="bg-zinc-100 p-3 rounded text-xs overflow-x-auto">
{`git clone --recursive https://github.com/hejiajiudeeyu/delegated-execution-dev.git
cd delegated-execution-dev/repos/client
npm install

export DELEXEC_HOME="$HOME/.delexec-responder"
export OPS_PORT_SUPERVISOR=8189
DELEXEC_HOME="$DELEXEC_HOME" \\
OPS_PORT_SUPERVISOR="$OPS_PORT_SUPERVISOR" \\
npm run ops -- start`}
                </pre>
              </div>
            </Collapsible>

            <Note variant="warn">
              后续步骤继续使用已安装的 <code className="font-mono">delexec-ops</code>。如果你已经做过 Caller Quick Start，请用不同的
              <code className="font-mono"> DELEXEC_HOME </code> 和 <code className="font-mono">OPS_PORT_SUPERVISOR</code>
              避免冲突；或者直接复用同一个 supervisor，Responder 和 Caller 本来就可以共存。
            </Note>
          </Step>

          <Step
            num="02"
            title="注册一个 Responder 账号"
            intent="Local Mode 下不需要联通 Platform；想直接对接 catalog 路由，把 --local 换成 --platform 即可。"
          >
            <CodeBlock language="bash" caption="纯本地">{`delexec-ops auth register --local --email me@example.com`}</CodeBlock>

            <CodeBlock language="bash" caption="对接自建 Platform">{`delexec-ops bootstrap \\
  --email me@example.com \\
  --platform http://127.0.0.1:8080`}</CodeBlock>
          </Step>

          <Step
            num="03"
            title="启用 Responder 角色"
            intent="一次启用，supervisor 会创建 responder controller 进程，并准备好 hotline 注册槽位。"
          >
            <CodeBlock language="bash">{`delexec-ops enable-responder \\
  --display-name "Acme Workspace Tools"`}</CodeBlock>
          </Step>

          <Step
            num="04"
            title="用 example hotline 验证链路"
            intent="先借现成的 workspace-summary 示例 hotline 把链路跑通，确保 supervisor + responder 之间通讯正常，再去写你自己的 hotline。"
          >
            <CodeBlock language="bash">{`delexec-ops add-example-hotline

BASE="http://127.0.0.1:\${OPS_PORT_SUPERVISOR:-8079}"
OPS_SESSION=$(curl -s -X POST "$BASE/auth/session/setup" \\
  -H 'content-type: application/json' \\
  -d '{"passphrase":"client-localtest-123"}' | jq -r '.token')
export OPS_SESSION

curl -s -X POST "$BASE/requests/example" \\
  -H 'content-type: application/json' \\
  -H "X-Ops-Session: $OPS_SESSION" \\
  -d '{}'`}</CodeBlock>

            <Note>
              这一步等价于「Caller 调你」。看到
              <code className="font-mono"> status: SUCCEEDED </code>
              说明 caller↔supervisor↔responder 三段链路全通。
            </Note>
          </Step>

          <Step
            num="05"
            title="写一份自己的 hotline registration"
            intent="hotline 的真实部署形态是：一份 process 或 http 适配器 + input_schema / output_schema。supervisor 会把它存到 DELEXEC_HOME。"
          >
            <CodeBlock language="bash" caption="本地 process 适配器（最快）">{`delexec-ops add-hotline \\
  --type process \\
  --hotline-id your.namespace.tool-name.v1 \\
  --display-name "工作区摘要器" \\
  --cmd "node ./my-tool/index.js" \\
  --cwd "$PWD"`}</CodeBlock>

            <CodeBlock language="bash" caption="HTTP 适配器（已有 web service）">{`delexec-ops add-hotline \\
  --type http \\
  --hotline-id your.namespace.tool-name.v1 \\
  --display-name "工作区摘要器" \\
  --url http://127.0.0.1:9000/run`}</CodeBlock>

            <Note>
              生成的 registration draft 落在
              <code className="font-mono"> $DELEXEC_HOME/hotline-registration-drafts/*.registration.json </code>，
              可以手动改 <code className="font-mono">input_schema / output_schema / summary / examples</code> 等字段。
              如果 <code className="font-mono">--cmd</code> 使用相对路径，请同时传
              <code className="font-mono"> --cwd </code>
              固定执行目录，避免 Responder 从别的目录重启后找不到脚本。
            </Note>
          </Step>

          <Step
            num="06"
            title="可选高级步骤：登记到自建 Platform · 提交审核"
            intent="本地 OK 之后，只有在已经接入自建 Platform 或托管 Platform 时，才把 hotline 提交审核。Platform 会做 schema 校验和 review_test，确认合规后可被 Caller 发现。"
          >
            <CodeBlock language="bash">{`delexec-ops submit-review \\
  --hotline-id your.namespace.tool-name.v1`}</CodeBlock>

            <CodeBlock language="json" caption="幕后真实 POST body 形状（节选）">{HOTLINE_ONBOARDING_BODY}</CodeBlock>

            <Note variant="warn">
              没有自建 Platform？也行。你的 hotline 仍然可以在本机或企业内 Selfhost 模式下被 Caller 调用，
              此时它走的是 supervisor 的私有 catalog，不需要公网连接。
            </Note>
          </Step>

          <Step
            num="07"
            title="OPC #0 生产演练：提交到 callanything.xyz"
            intent="如果你正在参加公开生产演练，使用线上 Platform endpoint，把 hotline_id 和 responder_id 交给 operator 审核；不要在聊天、issue 或文档里粘贴 API key。"
          >
            <CodeBlock language="bash" caption="生产 Platform 提交审核">{`PLATFORM="https://callanything.xyz/platform"
RESPONDER_EMAIL="me"@"example.com"

delexec-ops auth register \\
  --platform "$PLATFORM" \\
  --email "$RESPONDER_EMAIL"

delexec-ops enable-responder \\
  --display-name "Acme Workspace Tools"

delexec-ops add-hotline \\
  --type process \\
  --hotline-id your.namespace.tool-name.v1 \\
  --display-name "工作区摘要器" \\
  --cmd "node ./my-tool/index.js" \\
  --cwd "$PWD" \\
  --fixed-price-cents 50 \\
  --currency PTS \\
  --billing-disclosure-url "https://callanything.xyz/marketplace/responders/your-namespace"

delexec-ops submit-review \\
  --hotline-id your.namespace.tool-name.v1

delexec-ops status`}</CodeBlock>

            <CodeBlock language="bash" caption="审核通过后：用公网 Relay 保持 Responder 在线">{`RELAY="https://callanything.xyz/relay"
export TRANSPORT_TYPE="relay_http"
export TRANSPORT_BASE_URL="$RELAY"

# 前台运行：适合第一次看日志
delexec-ops start

# 或长运行：适合 SSH / 临时 shell 退出后继续接任务
mkdir -p "$DELEXEC_HOME/logs"
nohup env \\
  TRANSPORT_TYPE="relay_http" \\
  TRANSPORT_BASE_URL="$RELAY" \\
  delexec-ops start \\
  > "$DELEXEC_HOME/logs/public-relay-responder.log" 2>&1 &`}</CodeBlock>

            <CodeBlock language="bash" caption="公开可见性检查">{`curl -s "https://callanything.xyz/marketplace/hotlines" | jq
curl -s "$PLATFORM/v1/catalog/hotlines/your.namespace.tool-name.v1" | jq`}</CodeBlock>

            <Note>
              <span className="font-black">operator handoff：</span>
              提交后把 <code className="font-mono">responder_id</code>、
              <code className="font-mono"> hotline_id</code>、draft 里的
              <code className="font-mono"> pricing_hint</code> 和联系方式交给 operator。
              operator 需要完成审批、启用和 Caller 充值；如果公开 <code className="font-mono">/console/</code>
              还没有开放完整运营面，就走 operator 提供的人工通道。审核通过后，Responder 必须用
              <code className="font-mono"> relay_http </code>
              指向 <code className="font-mono">https://callanything.xyz/relay</code> 保持本机适配器在线。
            </Note>
          </Step>

          <Step
            num="08"
            title="检查在线状态"
            intent="供给方关心的另一件事：你这根 hotline 是不是真的在响应。supervisor 提供整体状态视图，Console 里有更细的健康灯。"
          >
            <CodeBlock language="bash">{`delexec-ops status

# 或 HTTP 直查
curl -s "$BASE/responder" -H "X-Ops-Session: $OPS_SESSION" | jq`}</CodeBlock>
          </Step>
        </div>

        <div className="mt-12 border-[6px] border-black bg-[#3B82F6]/85 backdrop-blur-md p-6 sm:p-8 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="text-2xl font-black uppercase text-white sm:text-3xl">下一步</h2>
          <p className="mt-3 text-sm font-bold text-white sm:text-base">
            你已经有一根本地可调用的 hotline draft。从这里推荐两条路：
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="border-4 border-black bg-white/95 backdrop-blur-sm p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500">把 hotline 设计好</p>
              <p className="mt-2 text-sm font-bold text-black">
                input_schema / output_schema / examples / 风险标签 决定了 Caller 能不能把你的能力用得稳。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <NextCta href="/docs" label="Hotline 设计文档" />
                <NextCta href="/console" label="看 Console 原型" tone="white" />
              </div>
            </div>
            <div className="border-4 border-black bg-white/95 backdrop-blur-sm p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500">体验 Caller 视角</p>
              <p className="mt-2 text-sm font-bold text-black">
                做能力的人也该自己当一次 Caller。换条路 5 分钟看看协议从 Agent 那一端长什么样。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <NextCta href="/docs/quick-start-caller" label="进 Caller Quick Start" />
                <NextCta href="/marketplace" label="逛 Marketplace" tone="white" />
              </div>
            </div>
          </div>
        </div>

        <FaqSection
          eyebrow="Responder FAQ"
          title="Responder Quick Start 常见问题"
          intro="这些问题覆盖了发布第一根 Hotline 时最常见的决策点：要不要接 Platform、什么时候提交审核、哪些字段最关键。"
          items={ROUTE_FAQS["/docs/quick-start-responder/"]}
          accent="#3B82F6"
        />
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
